/**
 * Arquivos da nota autorizada: Visualizar DANFE, Baixar DANFE, Baixar XML e Baixar PDF e XML
 * (ZIP). Cada botão é um LINK DIRETO para o arquivo já preparado (ver `emission-actions`):
 * o clique da pessoa vai ao link e o navegador trata como ação do usuário — não cai no
 * bloqueio de downloads automáticos. Erros e arquivos inválidos aparecem em texto claro.
 */
import { useState } from "react";
import { Icon } from "@/components/icons";
import { Alert, Button, LinkButton } from "@/components/ui";
import { errorMessage } from "@/lib/api";
import {
  usePreparedFiles,
  useObjectUrl,
  type DocForFiles,
  type PreparedFile,
} from "./emission-actions";

const LABEL: Record<PreparedFile["kind"], string> = {
  danfe_pdf: "DANFE (PDF)",
  danfe_html: "DANFE",
  xml: "XML autorizado",
  zip: "PDF e XML (ZIP)",
};

export function FiscalFileLinks({ doc }: { doc: DocForFiles }) {
  const files = usePreparedFiles(doc);
  const [started, setStarted] = useState<PreparedFile | null>(null);
  const danfeUrl = useObjectUrl(files.data?.danfe?.blob);
  const xmlUrl = useObjectUrl(files.data?.xml?.blob);
  const zipUrl = useObjectUrl(files.data?.zip?.blob);

  if (files.isPending) {
    return (
      <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
        <span
          aria-hidden
          className="size-4 animate-spin rounded-full border-2 border-current/25 border-t-current"
        />
        Preparando o DANFE e o XML para abrir e baixar…
      </p>
    );
  }
  if (files.isError) {
    return (
      <div className="space-y-2">
        <Alert tone="error">
          Não foi possível obter os arquivos da nota: {errorMessage(files.error)}. A nota continua
          autorizada.
        </Alert>
        <Button variant="secondary" onClick={() => void files.refetch()}>
          Tentar de novo
        </Button>
      </div>
    );
  }
  const data = files.data;
  if (!data) return null;
  const danfe = data.danfe;
  const xml = data.xml;
  const zip = data.zip;
  const link = (f: PreparedFile, url: string | null) => ({
    href: url ?? undefined,
    download: f.name,
    onClick: () => setStarted(f),
    "aria-disabled": url === null || undefined,
  });

  return (
    <div className="space-y-2">
      {data.problems.length > 0 && (
        <Alert tone="warn">
          <ul className="list-disc space-y-0.5 pl-5">
            {data.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Alert>
      )}
      <div className="flex flex-wrap gap-2" aria-label="Arquivos da nota">
        {danfe?.kind === "danfe_pdf" && danfeUrl && (
          <LinkButton variant="primary" href={danfeUrl} target="_blank" rel="noopener">
            <Icon name="fiscal" size={18} />
            Visualizar DANFE
          </LinkButton>
        )}
        {danfe && (
          <LinkButton {...link(danfe, danfeUrl)}>
            <Icon name="download" size={17} />
            Baixar DANFE
          </LinkButton>
        )}
        {xml && (
          <LinkButton {...link(xml, xmlUrl)}>
            <Icon name="download" size={17} />
            Baixar XML
          </LinkButton>
        )}
        {zip && (
          <LinkButton {...link(zip, zipUrl)}>
            <Icon name="download" size={17} />
            Baixar PDF e XML (ZIP)
          </LinkButton>
        )}
      </div>
      {started && (
        <p role="status" className="text-sm text-muted-foreground">
          Download iniciado: <strong>{started.name}</strong> ({LABEL[started.kind]}). Confira a
          barra ou a pasta de downloads. Se nada aparecer, o navegador pode ter bloqueado: no
          Chrome, toque no ícone de download bloqueado na barra de endereço e escolha “Permitir”.
        </p>
      )}
    </div>
  );
}
